import { Box, Stack, Typography, TypographyProps } from '@mui/material';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import ContentEditable from 'react-contenteditable';

import {
  EditButton,
  EditButtonProps,
} from '@/components/form/baseInputs/Button/EditButton';
import { sanitizeHtmlStrings } from '@/utils/formatting/strings';

import { useFormFieldsDisabled } from '../context/formFieldsDisabled.context';
import { selectElementContents } from './EditableHeader.utils';

export interface EditableHeaderProps {
  value: string;
  onChange: (newValue: string) => void;
  disabled?: boolean;
  testId?: string;
  typographyVariant?: TypographyProps['variant'];
  typographyProps?: TypographyProps;
  color?: TypographyProps['color'];
  onClick?: () => void;
  placeholder?: string;
  showPencilIcon?: boolean;
  editButtonSize?: EditButtonProps['size'];
  editButtonSpacing?: number;
  showEditBorder?: boolean;
  /** When true, HTML content is preserved during editing rather than being
   *  stripped by sanitizeHtmlStrings. The consumer is responsible for
   *  stripping HTML from the onChange value when needed. */
  preserveHtml?: boolean;
  /** Fires on every keystroke with the current sanitized content. Useful for
   *  enabling dirty-state tracking before the field is blurred. */
  onInput?: (currentValue: string) => void;
  /** When true, Enter inserts a line break instead of committing the edit. */
  allowLineBreaks?: boolean;
}

type ContentEditableRefType = ((instance: ContentEditable | null) => void) &
  RefObject<HTMLDivElement | null>;

export function EditableHeader({
  value,
  onChange,
  disabled,
  testId = 'EditableHeader-input',
  typographyVariant = 'title-xl',
  typographyProps,
  color,
  onClick,
  placeholder,
  showPencilIcon = true,
  editButtonSize = 'sm',
  editButtonSpacing = 1,
  showEditBorder = true,
  preserveHtml = false,
  onInput,
  allowLineBreaks = false,
}: EditableHeaderProps) {
  const { disabled: disabledExternal } = useFormFieldsDisabled();
  const editableContent = useRef<string>(value ?? '');
  const contentEditableRef = useRef<ContentEditable | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  // Track whether the user has made edits that haven't been saved via onBlur
  // yet. When true, the value-sync effect below skips overwriting the ref so
  // a parent re-render can't silently discard in-flight edits.
  const hasPendingEditsRef = useRef(false);

  useEffect(() => {
    if (!hasPendingEditsRef.current) {
      editableContent.current = value;
    }
  }, [value]);

  const isDisabled = disabled || disabledExternal;
  const showPlaceholder = placeholder && !isEditing && !value?.trim();

  const syncChangeEvent = useCallback((element: HTMLElement) => {
    element.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  const insertLineBreakAtCaret = useCallback(
    (element: HTMLElement) => {
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return false;
      }

      const range = selection.getRangeAt(0);
      range.deleteContents();

      const br = document.createElement('br');
      range.insertNode(br);
      range.setStartAfter(br);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      syncChangeEvent(element);
      return true;
    },
    [syncChangeEvent]
  );

  const handlePencilClick = useCallback(() => {
    onClick?.();
    // the `el` in this component is typed as `any`, so need to access it very carefully
    contentEditableRef.current?.el?.current &&
      selectElementContents(
        contentEditableRef.current.el.current as HTMLDivElement
      );
  }, [onClick]);

  return (
    <Stack direction="row" spacing={editButtonSpacing} alignItems="center">
      <Typography
        variant={typographyVariant}
        color={color}
        {...typographyProps}
      >
        <ContentEditable
          ref={contentEditableRef as unknown as ContentEditableRefType}
          role="textbox"
          html={showPlaceholder ? placeholder : editableContent.current}
          title="Click to edit"
          onFocus={() => {
            setIsEditing(true);
            onClick?.();
          }}
          onChange={(e) => {
            hasPendingEditsRef.current = true;
            if (preserveHtml) {
              editableContent.current = e.target.value;
            } else {
              const sanitizedContent = sanitizeHtmlStrings(e.target.value);
              if (sanitizedContent !== null) {
                editableContent.current = sanitizedContent;
              }
            }
            onInput?.(editableContent.current);
          }}
          tagName="span"
          data-testid={testId}
          onBlur={() => {
            hasPendingEditsRef.current = false;
            setIsEditing(false);
            if (editableContent.current !== value) {
              onChange(editableContent.current);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (
                allowLineBreaks &&
                (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229)
              ) {
                return;
              }

              e.preventDefault();
              if (allowLineBreaks) {
                const inserted =
                  (typeof document.execCommand === 'function' &&
                    document.execCommand('insertLineBreak')) ||
                  insertLineBreakAtCaret(e.currentTarget);
                if (!inserted) {
                  syncChangeEvent(e.currentTarget);
                }
              } else {
                e.currentTarget.blur();
              }
            } else if (allowLineBreaks && e.key === 'Escape') {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          style={{
            padding: '4px',
            ...(allowLineBreaks && {
              whiteSpace: 'pre-wrap',
              display: 'inline-block',
              padding: '0 4px',
            }),
            ...(showPlaceholder && { opacity: 0.5 }),
            ...(!showEditBorder && { outline: 'none' }),
          }}
          disabled={isDisabled}
        />
      </Typography>
      {showPencilIcon && (
        <Box>
          <EditButton
            disabled={isDisabled}
            size={editButtonSize}
            onClick={handlePencilClick}
          />
        </Box>
      )}
    </Stack>
  );
}
